import {LinkService} from '../../i18n/link.service'
import {PUBLIC_ROUTES} from '../../../shared/routing/public.routes'
import {OfferingsSection, OfferingsSectionItem} from '../types/offering-section.types'
import {
  OfferingsSectionFieldsFragment,
  OfferingsSectionItemCollectionFragment,
} from '../../../../__generated__/datocms.types'

export function parseOfferingsSection(
  linkService: LinkService,
  section?: OfferingsSectionFieldsFragment,
): OfferingsSection | undefined {
  if (!section) {
    return undefined
  }

  return {
    __type: 'OfferingsSection',
    id: section.id,
    title: section.title!,
    plot: section.plot!,
    items: parseOfferingsItemCollection(linkService, section.items2),
  }
}

function parseOfferingsItemCollection(
  linkService: LinkService,
  items: OfferingsSectionItemCollectionFragment[],
): OfferingsSectionItem[] {
  return (
    items
      .filter(
        item =>
          item?.__typename === 'OfferingModelRecord' ||
          item?.__typename === 'OfferingDetailPageModelRecord' ||
          item?.__typename === 'OfferingSectionItemModelRecord',
      )
      .map(item => {
        if (!item) {
          throw new Error('Could not parse offering section item')
        }
        switch (item.__typename) {
          case 'OfferingModelRecord':
            return {
              title: item.title!,
              icon: {
                name: item.icon!.name!,
                collection: item.icon!.collection!,
              },
              description: undefined,
              targetPath: pathToRequestOffering(linkService, item.offeringId!),
              targetType: 'job-request',
            }
          case 'OfferingDetailPageModelRecord':
            return {
              title: item.offering!.title!,
              icon: {
                name: item.offering!.icon!.name!,
                collection: item.offering!.icon!.collection!,
              },
              description: item.description,
              targetPath: pathToOfferingDetailPage(linkService, item.slug!),
              targetType: 'offering-page',
            }
          case 'OfferingSectionItemModelRecord': {
            const target = item.target!
            const path = () => {
              switch (target.__typename) {
                case 'OfferingModelRecord':
                  return pathToRequestOffering(linkService, target.offeringId!)
                case 'OfferingDetailPageModelRecord':
                  return pathToOfferingDetailPage(linkService, target.slug!)
                default:
                  throw new Error('Could not parse offering section item')
              }
            }
            const targetType = () => {
              switch (target.__typename) {
                case 'OfferingModelRecord':
                  return 'job-request'
                case 'OfferingDetailPageModelRecord':
                  return 'offering-page'
                default:
                  throw new Error('Could not parse offering section item')
              }
            }
            return {
              title: item.title!,
              icon: {
                name: item.icon!.name!,
                collection: item.icon!.collection!,
              },
              description: item.description,
              targetPath: path(),
              targetType: targetType(),
            }
          }
          default: {
            throw new Error('Could not parse offering section item')
          }
        }
      }) ?? []
  )
}

export function pathToRequestOffering(linkService: LinkService, offeringId: string): string {
  return linkService.localizedPathTo(PUBLIC_ROUTES.JOB_REQUEST, {
    routeParams: {offeringId},
  })
}

function pathToOfferingDetailPage(linkService: LinkService, slug: string): string {
  return linkService.localizedPathTo(PUBLIC_ROUTES.OFFERING_DETAIL_PAGE, {
    routeParams: {pageSlug: slug},
  })
}
